import { StatusLabel } from '@euler/app/components/StatusLabel';
import { Typography } from '@euler/app/components/typography';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { HStack } from '@euler/components/layout/Stack';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CommonTaskStatus } from '@euler/model/enum';
import { memo, ReactNode } from 'react';
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

export type DetailRowHeadInfo = {
  label: string;
  content: string | ReactNode;
};

export const DetailRowHead = memo(
  ({
    title,
    subTitle,
    titleNumberOfLines,
    style,
    titleStyle,
    subTitleStyle,
    status,
    statusText,
    infoRows,
    children,
    onPress,
  }: {
    title: string;
    subTitle?: string;
    titleNumberOfLines?: number;
    status?: CommonTaskStatus;
    statusText?: string;
    children?: ReactNode;
    infoRows?: DetailRowHeadInfo[];
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
    subTitleStyle?: StyleProp<TextStyle>;
    onPress?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const {
      bg,
      titleTextColor,
      labelTextColor,
      valueTextColor,
      statusTextColor,
    } = theme.task.scrollRow.head;
    const statusBgColor =
      status != null
        ? theme.statusBadge.construction[
            status === CommonTaskStatus.Pending
              ? 'pending'
              : status === CommonTaskStatus.InProgress
              ? 'inprogress'
              : 'finished'
          ]
        : undefined;
    return (
      <TouchableOpacity
        css={`
          justify-content: center;
          align-items: center;
          border-top-right-radius: ${rpx(20)}px;
          border-bottom-right-radius: ${rpx(20)}px;
          background-color: ${bg};
          padding: ${rpx(32)}px;
          width: ${rpx(290)}px;
        `}
        style={style}
        onPress={onPress}
      >
        <Typography.Label
          medium
          size={rpx(32)}
          color={titleTextColor}
          style={titleStyle}
          numberOfLines={titleNumberOfLines}
          allowFontScaling={titleNumberOfLines === 1}
          adjustsFontSizeToFit={titleNumberOfLines === 1}
          css={`
            margin-bottom: ${rpx(10)}px;
            text-align: center;
          `}
        >
          {title}
        </Typography.Label>
        {subTitle != null && (
          <Typography.Label
            regular
            size={rpx(24)}
            color={titleTextColor}
            style={subTitleStyle}
          >
            {subTitle}
          </Typography.Label>
        )}
        {infoRows?.map((row, i) => (
          <HStack key={i}>
            <Typography.Label regular size={rpx(20)} color={labelTextColor}>
              {`${row.label}: `}
            </Typography.Label>
            <MaybeText
              text={row.content}
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                color: ${valueTextColor};
                font-size: ${rpx(20)}px;
              `}
            />
          </HStack>
        ))}
        {status != null && statusBgColor != null ? (
          <StatusLabel
            text={statusText}
            status={status}
            textStyle={{
              fontSize: rpx(20),
              color: statusTextColor,
              lineHeight: rpx(30),
            }}
            css={`
              background-color: ${statusBgColor};
              margin-top: ${rpx(26)}px;
              padding: 0 ${rpx(28)}px;
              height: ${rpx(40)}px;
            `}
          />
        ) : null}
        {children}
      </TouchableOpacity>
    );
  },
);
